// Contract/ContractService.ts
import firebase from 'firebase/compat/app';
import { format, parseISO } from 'date-fns';
import { Milestone, ProposedDates } from './Contract.interface';

export class ContractService {
  static async updateInspectionDates(
    contractId: string,
    milestones: Milestone[],
    dates: ProposedDates,
    formatProposedDate: (date: any) => string
  ): Promise<Milestone[]> {
    const firestore = firebase.firestore();
    const contractRef = firestore.collection('contracts').doc(contractId);

    const updatedMilestones = milestones.map(milestone => {
      const baseMilestone = {
        id: milestone.id,
        sequence: milestone.sequence,
        title: milestone.title,
        type: milestone.type,
        assigneeId: milestone.assigneeId,
        assigneeType: milestone.assigneeType,
        date: milestone.date,
        status: milestone.status,
        amount: milestone.amount
      };

      if (milestone.id === 'inspection-setup') {
        return {
          ...baseMilestone,
          status: 'completed' as const,
          date: new Date().toISOString(),
          proposedDates: {
            preferred: dates.preferred,
            secondary: dates.secondary,
            tertiary: dates.tertiary
          },
          description: `Proposed dates:\n` +
            `Preferred: ${formatProposedDate(dates.preferred)}\n` +
            `Secondary: ${formatProposedDate(dates.secondary)}\n` +
            `Tertiary: ${formatProposedDate(dates.tertiary)}`
        };
      }

      if (milestone.id === 'inspection-date-selection') {
        return {
          ...baseMilestone,
          status: 'active' as const,
          date: new Date().toISOString(),
          description: 'Please select one of the proposed inspection dates'
        };
      }

      return baseMilestone;
    });

    const cleanUpdate = {
      milestones: updatedMilestones.map(milestone => {
        const cleaned: any = {};
        Object.entries(milestone).forEach(([key, value]) => {
          if (value !== undefined) {
            cleaned[key] = value;
          }
        });
        return cleaned;
      })
    };

    await contractRef.update(cleanUpdate);
    return updatedMilestones;
  }

  static async updateSelectedInspectionDate(
    contractId: string,
    milestones: Milestone[],
    selectedDate: string,
    isContractor: boolean,
    onPostInspectionAction: () => void
  ): Promise<Milestone[]> {
    const firestore = firebase.firestore();
    const contractRef = firestore.collection('contracts').doc(contractId);

    const selectedDateISO = parseISO(selectedDate).toISOString();

    const updatedMilestones: Milestone[] = milestones.map(milestone => {
      const baseMilestone: Milestone = {
        id: milestone.id,
        sequence: milestone.sequence,
        title: milestone.title,
        type: milestone.type,
        assigneeId: milestone.assigneeId,
        assigneeType: milestone.assigneeType,
        date: milestone.date || new Date().toISOString(),
        status: milestone.status,
        amount: milestone.amount
      };

      if (milestone.id === 'inspection-date-selection') {
        return {
          ...baseMilestone,
          status: 'completed' as const,
          date: selectedDateISO,
          selectedDate: selectedDate,
          description: `Inspection scheduled for ${format(parseISO(selectedDate), 'MMM dd, yyyy')}`
        };
      }

      if (milestone.id === 'post-inspection-contractor') {
        return {
          ...baseMilestone,
          status: 'active' as const,
          date: new Date().toISOString(),
          description: 'Contractor to review inspection and provide decision',
          action: isContractor ? {
            label: 'Post-Inspection Action',
            type: 'warning' as const,
            onClick: onPostInspectionAction,
            disabled: false,
            show: true
          } : undefined
        };
      }

      return baseMilestone;
    });

    const cleanUpdate = {
      milestones: updatedMilestones.map(milestone => {
        const cleaned: any = {};
        Object.entries(milestone).forEach(([key, value]) => {
          if (value !== undefined) {
            cleaned[key] = value;
          }
        });
        return cleaned;
      })
    };

    await contractRef.update(cleanUpdate);
    return updatedMilestones;
  }
}